import * as THREE from "three";
import { angleLerp } from "@tank/shared";

/**
 * Chase camera that trails the AIM direction (not the hull), pulled back and
 * up, with decaying screen shake. The hull keeps turning independently under
 * WASD; the camera orients to where the turret points so the view frames what
 * you're shooting at.
 *
 * `aimAngle` is smoothed into `camYaw` so flicking the mouse doesn't whip the
 * view — it eases. Position then eases toward the behind-aim spot, and the
 * look target is nudged forward along the aim so the danger zone is framed
 * rather than dead-centered on the tank.
 */
export class CameraRig {
  private target = new THREE.Vector3();
  private desired = new THREE.Vector3();
  private camYaw = 0;
  private shake = 0;
  private readonly maxShake = 2.5;

  // Behind/above the tank, relative to the aim direction. Tuned to feel
  // "pulled back" while keeping the tank comfortably in the lower third.
  private readonly behind = 19;
  private readonly height = 16;
  // How far ahead of the tank the look target (and therefore the fixed
  // center-screen crosshair) sits. Was 4.5 — barely past the muzzle tip
  // (TANK.MUZZLE_FORWARD = 3), so the reticle read as "stuck to the barrel"
  // instead of a proper aim-downrange point. Pushed out to a distance that
  // reads as an actual engagement range. Bumped +3m again per playtest
  // feedback once the first push (4.5 -> 16) was confirmed correct.
  private readonly lead = 19;
  // Height of the look-at point (and therefore how far up/toward the horizon
  // the screen-center reticle reads). Was pinned at near-ground height (1.4),
  // which pitched the camera down at the dirt even after pushing `lead` out —
  // playtest feedback was the crosshair still read too low. Raised so the
  // view tilts more toward eye-level/the horizon.
  private readonly lookHeight = 6;
  // Exponential smoothing rates (1/s). Framerate-independent — the old code
  // used a flat 0.2-per-frame lerp, which made the camera lag noticeably more
  // at low framerates and swing noticeably faster/twitchier at high refresh
  // rates (the same fixed fraction applied more/less often per second). These
  // rates reproduce the original tuned feel at 60fps while staying consistent
  // at any framerate.
  private readonly YAW_RATE = 13.4;
  private readonly POS_RATE = 13.4;

  constructor(private camera: THREE.PerspectiveCamera) {}

  follow(x: number, z: number, aimAngle: number, dt: number): void {
    // Ease the view's yaw toward the (already-smoothed) aim; position is derived
    // from this yaw so the whole rig swings as one, not at two different rates.
    const yawK = 1 - Math.exp(-dt * this.YAW_RATE);
    const posK = 1 - Math.exp(-dt * this.POS_RATE);
    this.camYaw = angleLerp(this.camYaw, aimAngle, yawK);
    const fx = Math.sin(this.camYaw);
    const fz = Math.cos(this.camYaw);
    this.desired.set(x - fx * this.behind, this.height, z - fz * this.behind);
    this.camera.position.lerp(this.desired, posK);
    if (this.shake > 0.001) {
      this.camera.position.x += (Math.random() * 2 - 1) * this.shake;
      this.camera.position.y += (Math.random() * 2 - 1) * this.shake;
      this.camera.position.z += (Math.random() * 2 - 1) * this.shake;
      this.shake *= 0.85; // decay
    }
    // Look ahead of the tank along the aim so the crosshair area is centered.
    this.target.set(x + fx * this.lead, this.lookHeight, z + fz * this.lead);
    this.camera.lookAt(this.target);
  }

  addShake(amount: number): void {
    this.shake = Math.min(this.maxShake, this.shake + amount);
  }
}
